import {
    faCircle,
    faCrosshairs,
    faDotCircle,
    faWaveSine,
} from '@fortawesome/pro-regular-svg-icons';
import { faCubes, faFolder, faFolderOpen } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GroupType } from '@kcftech/prometheustypescriptapiclient';
import { AnalyticsGroupIcon } from 'components/svg_icon/AnalyticsGroupIcon';
import { HardwareIcon } from 'components/svg_icon/HardwareIcon';
import { TreeItemGroupLabelIcon } from 'pages/workbench/side_panel/workbench_tree/styled';
import * as React from 'react';
import { memo } from 'react';
import { useThemeValues } from 'styles/theme';
import { KCFColor } from 'styles/themes/KCFColor';

export type TreeItemIconProps = Readonly<{
    isCollectorDependentSystem?: boolean;
    treeItemType: GroupType;
    isSelected?: boolean;
    isExpanded: boolean;
}>;

type GroupIconProps = Readonly<{
    color: string;
    isCollectorDependantSystem?: boolean;
    isExpanded?: boolean;
}>;

const SystemIcon = ({ color, isCollectorDependantSystem }: GroupIconProps) => {
    if (isCollectorDependantSystem) {
        return (
            <FontAwesomeIcon
                icon={faCrosshairs}
                color={color}
                title="Collector Dependent System"
                aria-label="Collector Dependent System"
            />
        );
    }

    return <></>;
};

const AssemblyIcon = ({ color }: GroupIconProps) => (
    <FontAwesomeIcon icon={faCubes} size="sm" color={color} className="px-2" />
);

const AssetIcon = ({ color }: GroupIconProps) => (
    <HardwareIcon type="asset" color={color} style={{ minWidth: 16 }} />
);

const MonitoringPointIcon = ({ color }: GroupIconProps) => (
    <FontAwesomeIcon icon={faDotCircle} size="sm" color={color} className="px-2" />
);

const IndicatorGroupIcon = ({ color }: GroupIconProps) => (
    <FontAwesomeIcon color={color} icon={faCircle} className="f-8" />
);

const GroupIcon = ({ color, isExpanded }: GroupIconProps) => (
    <FontAwesomeIcon
        icon={isExpanded ? faFolderOpen : faFolder}
        size="sm"
        color={color}
    />
);

const IndicatorIcon = ({ color }: GroupIconProps) => (
    <FontAwesomeIcon color={color} icon={faWaveSine} className="f-8" />
);

const SensorIcon = ({ color }: GroupIconProps) => (
    <HardwareIcon type="sensor" color={color} style={{ minWidth: 16 }} />
);

export const TreeIcons = {
    SystemIcon,
    AssemblyIcon,
    AssetIcon,
    MonitoringPointIcon,
    IndicatorGroupIcon,
    AnalyticsGroupIcon,
    GroupIcon,
    IndicatorIcon,
    SensorIcon,
};

export type SimpleTreeItemType = keyof typeof GroupType | 'Indicator' | 'Sensor';

const treeItemIconMap: Record<SimpleTreeItemType, React.FC<GroupIconProps>> = {
    System: SystemIcon,
    Assembly: AssemblyIcon,
    Asset: AssetIcon,
    MonitoringPoint: MonitoringPointIcon,
    IndicatorGroup: IndicatorGroupIcon,
    AnalyticModel: AnalyticsGroupIcon,
    Group: GroupIcon,
    Indicator: IndicatorIcon,
    Sensor: SensorIcon,
};

export const SimpleTreeItemIcon = ({
    type,
    color,
}: {
    type: SimpleTreeItemType;
    color?: string;
}) => {
    const defaultColor = useThemeValues().treeviewIconColor;
    return treeItemIconMap[type]({ color: color ?? defaultColor });
};

function TreeItemGroupIcon({
    isCollectorDependentSystem,
    treeItemType,
    isSelected,
    isExpanded,
}: TreeItemIconProps) {
    const { treeviewIconColor } = useThemeValues();
    const iconColor = isSelected ? KCFColor.lightGrey3 : treeviewIconColor;
    const Icon = treeItemIconMap[treeItemType];

    return (
        <TreeItemGroupLabelIcon
            $hasIcon={treeItemType !== GroupType.System || isCollectorDependentSystem}
        >
            <Icon
                color={iconColor}
                isCollectorDependantSystem={isCollectorDependentSystem}
                isExpanded={isExpanded}
            />
        </TreeItemGroupLabelIcon>
    );
}

const memoTreeItemGroupIcon = memo(TreeItemGroupIcon);
export { memoTreeItemGroupIcon as TreeItemGroupIcon };
